import { HTMLAttributes, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import ReactQuill, { Quill } from 'react-quill';
import styled from '@emotion/styled';
import { MDSIcon, MDSLoadingIndicator, MDSTypography } from '@marqvision/mds-v2';
import { MDSThemeColorPath } from '@marqvision/mds-v2/types';
import { AddLinkPopup } from './@components/AddLinkPopup';
import { useViewModel } from './@hooks/useViewModel';
import { LabelType, Module, Toolbar, ToolbarPosition } from './@types';
import { parseToolbarContainer } from './@utils';
import './@module';
import { DeltaOperation, DeltaStatic, StringMap } from 'quill';

const Delta = Quill.import('delta');

type StylesProps = {
  minHeight?: string | number;
  maxHeight?: string | number;
  toolbar: { position: ToolbarPosition; isSticky: boolean };
};

const Styled = {
  Root: styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
  `,
  TitleBox: styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
  `,
  Label: styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
  `,
  EditorWrap: styled.div<StylesProps>`
    position: relative;
    & .quill {
      color: ${({ theme }) => theme.color.content.neutral.default.normal};
      background-color: ${({ theme }) => theme.comp.input.color.bg.normal};
      display: flex;
      flex-direction: ${({ toolbar }) => (toolbar.position === 'top' ? 'column' : 'column-reverse')};
      border: 1px solid ${({ theme }) => theme.color.border.neutral.default.normal};
      border-radius: 8px;
      transition: 225ms ease;
      &.isDisabled {
        background-color: ${({ theme }) => theme.comp.input.color.bg.disabled};
        color: ${({ theme }) => theme.color.content.neutral.default.disabled};
        pointer-events: none;
        & svg {
          color: ${({ theme }) => theme.color.content.neutral.default.disabled};
        }
      }
      &.isReadOnly {
        background-color: ${({ theme }) => theme.comp.input.color.bg.disabled};
        & button {
          pointer-events: none;
        }
        & svg {
          color: ${({ theme }) => theme.color.content.neutral.default.disabled};
        }
      }
      &.focus {
        border-color: ${({ theme }) => theme.color.border.primary.default.normal};
        outline: 3px solid ${({ theme }) => theme._raw_color.blue200};
      }
      &.isError {
        border-color: ${({ theme }) => theme.color.border.critical.default.normal};
        &.focus {
          outline: 3px solid ${({ theme }) => theme._raw_color.red100};
        }
      }
      & .ql-editor {
        line-height: 1.5;
        font-size: 14px;
        min-height: ${({ minHeight }) => minHeight || '54px'};
        max-height: ${({ maxHeight }) => maxHeight || 'inherit'};
      }
      &.hideImageUpload .ql-image {
        display: none;
      }
      & .ql-snow .ql-tooltip {
        left: 4px !important;
        border: none;
        border-radius: 4px;
        box-shadow: 0 8px 16px 0 #00000033, 0 0 2px 0 #00000029;
      }
      & .ql-toolbar.ql-snow {
        position: ${({ toolbar }) => (toolbar.isSticky ? 'sticky' : undefined)};
        top: ${({ toolbar }) => (toolbar.position === 'top' ? 0 : undefined)};
        bottom: ${({ toolbar }) => (toolbar.position === 'bottom' ? 0 : undefined)};
        z-index: ${({ toolbar }) => (toolbar.isSticky ? 1 : undefined)};
        background-color: ${({ toolbar, theme }) =>
          toolbar.isSticky ? theme.color.bg.surface.neutral.default.normal : undefined};
        border-top-left-radius: ${({ toolbar }) => (toolbar.position === 'top' ? '8px' : undefined)};
        border-top-right-radius: ${({ toolbar }) => (toolbar.position === 'top' ? '8px' : undefined)};
        border-bottom-left-radius: ${({ toolbar }) => (toolbar.position === 'bottom' ? '8px' : undefined)};
        border-bottom-right-radius: ${({ toolbar }) => (toolbar.position === 'bottom' ? '8px' : undefined)};
        padding: 4px 8px;
        border: none;
        border-top: ${({ toolbar, theme }) =>
          toolbar.position === 'bottom' ? `1px solid ${theme.color.border.neutral.default.normal}` : undefined};
        border-bottom: ${({ toolbar, theme }) =>
          toolbar.position === 'top' ? `1px solid ${theme.color.border.neutral.default.normal}` : undefined};
        display: flex;
        gap: 12px;
        & .ql-formats {
          margin-right: 0;
          display: flex;
          gap: 4px;
          & button {
            padding: 2px;
            width: 24px;
            &.ql-active svg path {
              fill: ${({ theme }) => theme.color.content.primary.default.normal};
            }
          }
          &:not(:last-child):after {
            margin-left: 8px;
            content: '';
            border-right: 1px solid ${({ theme }) => theme.comp.divider.color.weak};
          }
        }
      }
      & .ql-container.ql-snow {
        border: none;
        font-family: unset;
      }
      & .ql-container .ql-editor {
        padding: 6px 11px;
      }
      & .ql-container .ql-editor.ql-blank:before {
        color: ${({ theme }) => theme.color.content.neutral.default.disabled};
        font-style: unset;
      }
      & u {
        text-decoration: underline;
      }
    }
  `,
  Loading: styled.div`
    position: absolute;
    top: 0;
    left: 0;
    z-index: 2;
    width: 100%;
    height: 100%;
    background-color: ${({ theme }) => theme.comp.dimmed.color.default};
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    & svg {
      color: white;
    }
  `,
  Guide: styled.div`
    padding: 0 4px;
    display: grid;
    grid-template-columns: auto 1fr;
    align-items: center;
    gap: 4px;
  `,
};

const DEFAULT_MODULES: Module[] | Module[][] = [
  ['bold', 'underline'],
  ['bullet', 'ordered'],
  ['link', 'image'],
];

const ALLOWED_ATTRS = ['bold', 'underline', 'list', 'indent', 'link'];

type Props = {
  label?: LabelType;
  value: string;
  toolbar?: Toolbar;
  maxLength?: number | false;
  placeholder?: string;
  minHeight?: string | number;
  maxHeight?: string | number;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  source?: 'api' | 'user' | 'silent';
  onChange: (value: string) => void;
  onGetUploadUrl?: (src: string) => Promise<string>;
  onGetPresignedUrl?: (src: string) => Promise<string>;
  onBlur?: () => void;
  status?: 'error' | 'idle';
  /** @description 하단 가이드 텍스트, <code>isError</code> 값에 따라 스타일 바뀜 */
  guide?: string;
} & Pick<HTMLAttributes<HTMLDivElement>, 'className'>;

export const QuillEditor = (props: Props) => {
  const {
    value = '',
    label: _label = '',
    maxLength: _maxLength,
    placeholder,
    minHeight: _minHeight,
    maxHeight: _maxHeight,
    source,
    onChange,
    onGetUploadUrl,
    onGetPresignedUrl,
    toolbar: _toolbar,
    onBlur,
    status,
    guide,
    ...restProps
  } = props;

  const minHeight = typeof _minHeight === 'string' ? _minHeight : _minHeight ? `${_minHeight}px` : undefined;
  const maxHeight = typeof _maxHeight === 'string' ? _maxHeight : _maxHeight ? `${_maxHeight}px` : undefined;

  const toolbar = {
    position: typeof _toolbar === 'object' ? _toolbar.position : _toolbar || 'top',
    modules: typeof _toolbar === 'object' && _toolbar.modules ? _toolbar.modules : DEFAULT_MODULES,
    isSticky: typeof _toolbar === 'object' && _toolbar.isSticky ? _toolbar.isSticky : false,
  };

  const [focus, setFocus] = useState(false);
  const [linkAnchor, setLinkAnchor] = useState<Element | null>(null);

  const quillRef = useRef<ReactQuill>(null);
  const editorWrapRef = useRef<HTMLDivElement>(null);

  const { selectedLinkLabel, isUploading, onClickLinkToolbar, onInsertLink, onInsertImage } = useViewModel({
    quill: quillRef,
    onGetUploadUrl,
    onGetPresignedUrl,
  });

  const { isError, guideColor, guideIcon } = useMemo(() => {
    const isError = status === 'error';
    const guideColor: MDSThemeColorPath = isError
      ? 'color/content/critical/default/normal'
      : 'color/content/neutral/default/normal';
    const guideIcon = isError ? (
      <MDSIcon.ErrorWarning variant="fill" size={16} color={guideColor} />
    ) : (
      <MDSIcon.Info variant="border" size={16} color={guideColor} />
    );

    return {
      isError,
      guideColor,
      guideIcon,
    };
  }, [status]);

  const maxLength = _maxLength === false ? Infinity : _maxLength || 10000;
  const valueLength = value.replace(/(<img\s+[^>]*src="[^"]+)\?[^"]*"/g, '$1"').length;
  const label = (() => {
    if (typeof _label === 'string') {
      return {
        main: _label,
        sub: undefined,
      };
    }
    return _label;
  })();

  const handleChange = (_value: string, _: unknown, _source: 'api' | 'user' | 'silent') => {
    if (source && source !== _source) {
      return;
    }
    if (_value.length > maxLength) {
      return;
    }
    if (_value === value) {
      return;
    }
    onChange(_value);
  };

  const handleBlur = () => {
    setFocus(false);
    handleSubmit();
    onBlur?.();
  };

  const handleLinkClick = useCallback(() => {
    const linkButton =
      editorWrapRef.current?.querySelector('button.ql-link.ql-active') ||
      editorWrapRef.current?.querySelector('button.ql-link');

    if (linkButton) {
      setLinkAnchor(linkButton);
      onClickLinkToolbar();
    }
  }, [onClickLinkToolbar]);

  const handleInsertLink = (value: { label: string; url: string }) => {
    onInsertLink(value);
    setLinkAnchor(null);
  };

  const handleSubmit = () => {
    if (quillRef.current?.value === value) {
      return;
    }
    onChange((quillRef.current?.value || '') as string);
  };

  useEffect(() => {
    const editor = editorWrapRef.current;

    if (!editor) {
      return;
    }

    editor.querySelectorAll('.ql-toolbar button').forEach((el) => {
      el.setAttribute('tabindex', '-1');
    });
  }, []);

  useEffect(() => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    const clipboard = quill.getModule('clipboard');

    clipboard.addMatcher(Node.ELEMENT_NODE, (_: unknown, delta: DeltaStatic) => {
      const newDelta = new Delta();

      delta.ops?.forEach((op: DeltaOperation) => {
        if (typeof op.insert !== 'string') {
          newDelta.push(op);
          return;
        }

        const attrs = op.attributes || {};

        const cleanAttrs: Record<string, StringMap> = {};

        Object.keys(attrs).forEach((key) => {
          if (ALLOWED_ATTRS.includes(key)) {
            cleanAttrs[key] = attrs[key];
          }
        });

        if (Object.keys(cleanAttrs).length > 0) {
          newDelta.insert(op.insert, cleanAttrs);
        } else {
          newDelta.insert(op.insert);
        }
      });

      return newDelta;
    });
  }, []);

  return (
    <div>
      <Styled.Root {...restProps}>
        {label && (
          <Styled.TitleBox>
            <Styled.Label>
              <MDSTypography variant="body" size="m" weight="medium" color="color/content/neutral/default/normal">
                {label.main}
              </MDSTypography>
              {label.sub && (
                <MDSTypography variant="body" size="s" color="color/content/neutral/secondary/normal">
                  ({label.sub})
                </MDSTypography>
              )}
            </Styled.Label>
            {_maxLength !== false && (
              <MDSTypography variant="body" size="s" color="color/content/neutral/secondary/normal">
                {valueLength}/{maxLength}
              </MDSTypography>
            )}
          </Styled.TitleBox>
        )}
        <Styled.EditorWrap
          data-typography-new-font={true}
          ref={editorWrapRef}
          minHeight={minHeight}
          maxHeight={maxHeight}
          toolbar={{ position: toolbar.position, isSticky: toolbar.isSticky }}
        >
          <ReactQuill
            readOnly={props.isReadOnly || props.isDisabled}
            ref={quillRef}
            value={value}
            className={clsx({
              focus,
              hideImageUpload: !onGetUploadUrl,
              isError,
              isDisabled: props.isDisabled,
              isReadOnly: props.isReadOnly,
            })}
            placeholder={placeholder || 'Enter content'}
            theme="snow"
            bounds="document.body"
            modules={{
              toolbar: {
                container: parseToolbarContainer(toolbar.modules),
                handlers: {
                  link: handleLinkClick,
                  image: onInsertImage,
                },
              },
            }}
            onChange={handleChange}
            onFocus={() => {
              if (props.isDisabled || props.isReadOnly) {
                return;
              }
              setFocus(true);
            }}
            onBlur={handleBlur}
          />
          {isUploading && (
            <Styled.Loading>
              <MDSLoadingIndicator size={24} />
            </Styled.Loading>
          )}
        </Styled.EditorWrap>
        {guide && (
          <Styled.Guide>
            {guideIcon}
            <MDSTypography color={guideColor} size="xs" weight="medium">
              {guide}
            </MDSTypography>
          </Styled.Guide>
        )}
      </Styled.Root>
      <AddLinkPopup
        open={linkAnchor}
        label={selectedLinkLabel}
        onSubmit={(value) => handleInsertLink(value)}
        onClose={() => setLinkAnchor(null)}
      />
    </div>
  );
};
